import { checkoutPlanIdFromWhopUrl } from "~/lib/whopCheckoutProxy";

export type PremiumAccessPlanKey =
  | "premium-monthly"
  | "premium-quarterly"
  | "premium-annual";

export type PremiumAccessPlan = {
  label: string;
  amount: string;
  amountCents: number;
  /** Calendar months one charge covers (1 / 3 / 12). */
  monthsPerCycle: number;
  period: string;
  billingNote: string;
  /**
   * The actual charge stated as a billing event ("billed $349 monthly"), for
   * running sentences in checkout chrome. Unlike billingNote it always carries
   * the amount, because a sentence has no price headline above it.
   */
  billedClause: string;
  note: string;
};

export const premiumAccessPlans = {
  "premium-monthly": {
    label: "Monthly",
    amount: "$349",
    amountCents: 34900,
    monthsPerCycle: 1,
    period: "/ month",
    billingNote: "Billed monthly",
    billedClause: "billed $349 monthly",
    note: "Cancel anytime",
  },
  "premium-quarterly": {
    label: "Quarterly",
    amount: "$900",
    amountCents: 90000,
    monthsPerCycle: 3,
    period: "/ quarter",
    billingNote: "Billed $900 every three months",
    billedClause: "billed $900 every three months",
    note: "Lower total than three monthly payments",
  },
  "premium-annual": {
    label: "Yearly",
    amount: "$3,300",
    amountCents: 330000,
    monthsPerCycle: 12,
    period: "/ year",
    billingNote: "Billed $3,300 yearly",
    billedClause: "billed $3,300 yearly",
    note: "Best annual rate",
  },
} as const satisfies Record<PremiumAccessPlanKey, PremiumAccessPlan>;

// ── Per-month display (owner's 2026-07-31 same-unit rule, corrected) ─────────
// The plan cards show every premium tier's price in one unit: what a month
// costs at that tier's rate ($349 / $300 / $275), DERIVED by dividing the
// billed amount by the months one charge covers. No per-month dollar figure
// is ever typed as a literal in a component. The true charge and its cadence
// stay visible in the card's subline (billingNote) and in Stripe's own form,
// which renders the real billed total. (The first cut of this rule multiplied
// up to a yearly figure; the owner corrected the direction the same day.)

export function formatUsdCents(cents: number): string {
  const whole = cents / 100;
  return Number.isInteger(whole)
    ? `$${whole.toLocaleString("en-US")}`
    : `$${whole.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
}

export function premiumPlanMonthlyCents(
  planKey: PremiumAccessPlanKey
): number {
  const plan = premiumAccessPlans[planKey];
  // Today's catalog divides to whole dollars (34900/1, 90000/3, 330000/12;
  // pinned in tests). If a future price does not divide cleanly, this rounds
  // to the nearest cent and formatUsdCents renders the cents — review that
  // display before shipping such a price.
  return Math.round(plan.amountCents / plan.monthsPerCycle);
}

/** The card's price line: per-month amount, one unit for every tier. */
export function premiumPlanDisplayPrice(planKey: PremiumAccessPlanKey): {
  amount: string;
  period: string;
} {
  return {
    amount: formatUsdCents(premiumPlanMonthlyCents(planKey)),
    period: "/ month",
  };
}

export const premiumAccessPlanKeys = Object.keys(
  premiumAccessPlans
) as PremiumAccessPlanKey[];

export function premiumPlanEnvValue(planKey: PremiumAccessPlanKey): string {
  // Next only replaces NEXT_PUBLIC_* references in browser bundles when the
  // property access is statically visible. A computed process.env lookup works
  // during SSR but becomes undefined after hydration, which disabled checkout
  // and left the modal without a URL in the browser.
  switch (planKey) {
    case "premium-monthly":
      return process.env.NEXT_PUBLIC_WHOP_PREMIUM_MONTHLY_PLAN ?? "";
    case "premium-quarterly":
      return process.env.NEXT_PUBLIC_WHOP_PREMIUM_QUARTERLY_PLAN ?? "";
    case "premium-annual":
      return process.env.NEXT_PUBLIC_WHOP_PREMIUM_YEARLY_PLAN ?? "";
  }
}

export function isPremiumAccessPlanKey(
  value: string | string[] | undefined
): value is PremiumAccessPlanKey {
  return (
    typeof value === "string" &&
    (premiumAccessPlanKeys as readonly string[]).includes(value)
  );
}

function planIdFromConfiguredValue(configured: string | undefined) {
  const value = configured?.trim();
  if (!value) return null;
  if (/^plan_[A-Za-z0-9]+$/.test(value)) return value;
  return checkoutPlanIdFromWhopUrl(value);
}

export function configuredPremiumPlanId(
  planKey: PremiumAccessPlanKey
): string | null {
  return planIdFromConfiguredValue(premiumPlanEnvValue(planKey));
}

export function configuredPremiumPlanIds(): string[] {
  return premiumAccessPlanKeys
    .map(configuredPremiumPlanId)
    .filter((planId): planId is string => Boolean(planId));
}

export function isConfiguredPremiumCheckoutPlan(plan: string): boolean {
  return configuredPremiumPlanIds().includes(plan);
}

/**
 * Reverse lookup for the dynamic checkout-URL resolver: a pasted Whop
 * checkout link or bare plan_… id maps back to the premium tier it sells, so
 * the owner's existing share links (and his habit of handing out
 * whop.com/checkout URLs) resolve to the same tier on the new own-Stripe
 * checkout. Non-premium plan ids return null and keep their legacy behavior.
 */
export function premiumPlanKeyFromPlanId(
  planId: string
): PremiumAccessPlanKey | null {
  for (const planKey of premiumAccessPlanKeys) {
    if (configuredPremiumPlanId(planKey) === planId) return planKey;
  }
  return null;
}

// Entry point for the own-Stripe checkout path (CHECKOUT_PROVIDER=stripe).
// Client-safe: a plain path, no env reads. The endpoint 303-redirects to
// Stripe's hosted checkout for the plan.
export function buildStripeCheckoutStartPath(
  planKey: PremiumAccessPlanKey
): string {
  return `/api/checkout/stripe/start?plan=${encodeURIComponent(planKey)}`;
}

export function configuredLifetimePlanId(): string | null {
  return planIdFromConfiguredValue(process.env.NEXT_PUBLIC_WHOP_LIFETIME_PLAN);
}

export function isConfiguredLifetimeCheckoutPlan(plan: string): boolean {
  const lifetimePlanId = configuredLifetimePlanId();
  return Boolean(lifetimePlanId && plan === lifetimePlanId);
}
